/// <amd-module name="Wit/DirectoryView/Components/DirectoryDropdown" />
import * as React from "react";
import { TeamProfilePanelIconButton } from "TFS/TeamProfilePanelIconButton/TeamProfilePanelIconButton";
import { format } from "VSS/Core/Util/String";
import { IVssComponentContext, VssComponent } from "VSS/Platform/Layout";
import { Dropdown, DropdownCallout, DropdownExpandableButton, IDropdownCalloutProps, IDropdownExpandableButtonProps } from "VSSUI/Dropdown";
import { Icon } from "VSSUI/Icon";
import { IListBoxItem } from "VSSUI/ListBox";
import { Observer, UncheckedObserver } from "VSSUI/Observer";
import { DropdownSelection } from "VSSUI/Utilities/DropdownSelection";
import { Location } from "VSSUI/Utilities/Position";
import { ISelectionRange } from "VSSUI/Utilities/Selection";
import { DirectoryItem } from "Wit/DirectoryService/Client/Contracts/Directory";
import { IDirectoryDropdownItem } from "Wit/DirectoryService/Models";
import { DirectoryFavoriteButton } from "Wit/DirectoryView/Components/DirectoryFavoriteButton";
import { DirectoryDropdownProvider } from "Wit/DirectoryView/DirectoryDropdownProvider";
import { isFeatureFlagEnabled } from "VSS/Platform/Feature";
import * as Resources from "Wit/DirectoryView/Resources";

export interface IDirectoryDropdownProps {
    calloutWidth?: number;
    hideFavorites?: boolean;
    hideIcon?: boolean;
    itemProvider: DirectoryDropdownProvider;
    onSelect?: (event: React.SyntheticEvent<HTMLElement, Event>, item: IListBoxItem<IDirectoryDropdownItem>) => void;
    selectedItem?: DirectoryItem;
}

export class DirectoryDropdown extends VssComponent<IDirectoryDropdownProps, {}> {
    private dropdown = React.createRef<Dropdown<IDirectoryDropdownItem>>();
    private dropdownSelection: DropdownSelection;
    private hasMadeInitialSelection = false;
    private isNewSprintsViewEnabled = isFeatureFlagEnabled(this.context.pageContext, "WebPlatform.WorkItemTracking.NewSprintsBacklogsView", false);

    constructor(props: IDirectoryDropdownProps, context: IVssComponentContext) {
        super(props, context);

        this.dropdownSelection = new DropdownSelection();
    }

    public render(): JSX.Element | null {
        const { itemProvider } = this.props;

        return (
            <UncheckedObserver
                itemProvider={{ observableValue: itemProvider, filter: this.onItemsUpdated }}
                selection={this.dropdownSelection}
                isLoading={itemProvider.isLoading}
            >
                {(props: { isLoading: boolean }) => {
                    const anchorOrigin = { horizontal: Location.start, vertical: Location.end };
                    const dropdownOrigin = { horizontal: Location.start, vertical: Location.start };
                    const { selectedItem, onSelect, hideFavorites, hideIcon, calloutWidth } = this.props;
                    return (
                        <Dropdown
                            actions={itemProvider.getActions()}
                            ref={this.dropdown}
                            onSelect={onSelect}
                            className={`directory-selector-dropdown ${this.isNewSprintsViewEnabled ? 'directory-dropdown-sprints-ff-enabled' : ''}`}
                            calloutContentClassName="directory-dropdown-content"
                            filterThrottleWait={100}
                            selection={this.dropdownSelection}
                            items={itemProvider}
                            loading={props.isLoading}
                            onExpand={() => itemProvider.loadData()}
                            renderExpandable={(props: IDropdownExpandableButtonProps) => (
                                <Observer selection={this.dropdownSelection}>
                                    {({ selection }: { selection: ISelectionRange[] }) => {
                                        const directory = selection.length > 0 && itemProvider.value[selection[0].beginIndex]?.data?.directory;
                                        const selectedDirectory = directory || selectedItem;
                                        return (
                                            <div className="directory-dropdown-expandable-button-container flex-row">
                                                <DropdownExpandableButton {...props} subtle={true}>
                                                    {!hideIcon && <Icon className="icon-large-margin" {...itemProvider.iconProps} />}
                                                    {/** Only show the name when we haven't loaded items and thus don't have an actual selection */}
                                                    {!directory && selectedItem && selectedItem.name}
                                                </DropdownExpandableButton>
                                                {!hideFavorites && selectedDirectory && itemProvider.favoriteType && (
                                                    <span className={`title-l flex-row ${this.isNewSprintsViewEnabled ? 'hide-on-mobile' : ''}`}>
                                                        <DirectoryFavoriteButton artifactType={itemProvider.favoriteType} item={selectedDirectory} />
                                                        <TeamProfilePanelIconButton teamId={selectedDirectory.id} teamName={selectedDirectory.name} />
                                                    </span>
                                                )}
                                            </div>
                                        );
                                    }}
                                </Observer>
                            )}
                            renderCallout={(props: IDropdownCalloutProps) => (
                                <DropdownCallout
                                    {...props}
                                    containerClassName="directory-selector-callout"
                                    anchorOrigin={anchorOrigin}
                                    dropdownOrigin={dropdownOrigin}
                                    filterPlaceholderText={format(Resources.SearchTeamsPlaceholder, itemProvider.artifactTypeName)}
                                    filteredNoResultsText={Resources.NoResultsFound}
                                />
                            )}
                            width={calloutWidth ? calloutWidth : 480}
                        />
                    );
                }}
            </UncheckedObserver>
        );
    }

    private makeInitialSelection = () => {
        const { itemProvider } = this.props;

        if (itemProvider.value.length > 0 && this.props.selectedItem) {
            // Select the item with the matching id
            for (let i = 0; i < itemProvider.value.length; i++) {
                if (itemProvider.value[i].data && itemProvider.value[i].data?.directory?.id === this.props.selectedItem.id) {
                    this.dropdownSelection.select(i);
                    this.hasMadeInitialSelection = true;
                    return;
                }
            }
        }
    };

    private onItemsUpdated = () => {
        if (!this.hasMadeInitialSelection) {
            this.makeInitialSelection();
        }
        return false;
    };
}
